import { z } from 'zod';

// Base message schema
const baseMessageSchema = z.object({
  id: z.string(),
  timestamp: z.string().datetime(),
  threadId: z.string(),
});

// Message parts (compatible with AI SDK v5)
const textPartSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});

const filePartSchema = z.object({
  type: z.literal('file'),
  mediaType: z.string(),
  url: z.string(), // Data URL
});

const messagePartSchema = z.discriminatedUnion('type', [textPartSchema, filePartSchema]);

// User message schema
export const userMessageSchema = z
  .object({
    type: z.literal('message'),
    role: z.literal('user'),
    parts: z.array(messagePartSchema),
  })
  .merge(baseMessageSchema);

// Assistant message schema
export const assistantMessageSchema = z
  .object({
    type: z.literal('message'),
    role: z.literal('assistant'),
    parts: z.array(messagePartSchema),
  })
  .merge(baseMessageSchema);

// Combined message schema
export const chatMessageSchema = z.discriminatedUnion('role', [userMessageSchema, assistantMessageSchema]);

// Client -> Server messages
export const clientMessageSchema = z.discriminatedUnion('type', [
  // User sends a chat message
  userMessageSchema,

  // Typing indicators
  z.object({
    type: z.literal('typing_start'),
    threadId: z.string(),
  }),

  z.object({
    type: z.literal('typing_stop'),
    threadId: z.string(),
  }),

  // Ping for heartbeat
  z.object({
    type: z.literal('ping'),
  }),
]);

// Server -> Client messages
export const serverMessageSchema = z.discriminatedUnion('type', [
  // User message (broadcast from server)
  z
    .object({
      type: z.literal('user_message'),
      role: z.literal('user'),
      parts: z.array(messagePartSchema),
      fromUserId: z.string(),
    })
    .merge(baseMessageSchema),

  // Assistant message (broadcast from server)
  z
    .object({
      type: z.literal('assistant_message'),
      role: z.literal('assistant'),
      parts: z.array(messagePartSchema),
    })
    .merge(baseMessageSchema),

  // AI response streaming
  z.object({
    type: z.literal('assistant_message_start'),
    messageId: z.string(),
    threadId: z.string(),
    timestamp: z.string().datetime(),
  }),

  z.object({
    type: z.literal('assistant_message_delta'),
    messageId: z.string(),
    delta: textPartSchema,
    threadId: z.string(),
  }),

  z.object({
    type: z.literal('assistant_message_complete'),
    messageId: z.string(),
    threadId: z.string(),
  }),

  // Typing indicators
  z.object({
    type: z.literal('user_typing'),
    userId: z.string(),
    isTyping: z.boolean(),
    threadId: z.string(),
  }),

  // Error messages
  z.object({
    type: z.literal('error'),
    message: z.string(),
    code: z.string().optional(),
  }),

  // Pong response
  z.object({
    type: z.literal('pong'),
  }),
]);

// Thread ID format: pr_owner_repo_pullNumber
export const threadIdSchema = z.string().regex(/^pr_[a-zA-Z0-9_-]+_[a-zA-Z0-9_.-]+_\d+$/);

// Inferred types
export type UserMessage = z.infer<typeof userMessageSchema>;
export type AssistantMessage = z.infer<typeof assistantMessageSchema>;
export type ChatMessage = z.infer<typeof chatMessageSchema>;
export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type ThreadId = z.infer<typeof threadIdSchema>;
export type MessagePart = z.infer<typeof messagePartSchema>;

// Utility functions
export const createThreadId = (owner: string, repo: string, pullNumber: number): ThreadId => {
  return `${owner}/${repo}/${pullNumber}` as ThreadId;
};

export const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Validation helpers
export const validateClientMessage = (data: unknown): ClientMessage => {
  return clientMessageSchema.parse(data);
};

export const validateServerMessage = (data: unknown): ServerMessage => {
  return serverMessageSchema.parse(data);
};

// AI SDK v5 UIMessage conversion helpers
export const toUIMessage = (wsMessage: ChatMessage): any => ({
  id: wsMessage.id,
  role: wsMessage.role,
  parts: wsMessage.parts,
  createdAt: new Date(wsMessage.timestamp),
});

export const fromUIMessage = (uiMessage: any, threadId: ThreadId): UserMessage => ({
  type: 'message',
  id: uiMessage.id || generateMessageId(),
  role: 'user',
  parts: uiMessage.parts || [{ type: 'text', text: '' }],
  timestamp: uiMessage.createdAt?.toISOString() || new Date().toISOString(),
  threadId,
});
